import Card from "@material-ui/core/Card";
import Collapse from "@material-ui/core/Collapse";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { RefObject, useEffect, useMemo, useRef, useState } from "react";
import { useScreenSize } from "../../../../../../../common/hooks/useScreenSize";
import { fetchPost } from "../../../../../../../common/util/fetch";
import { theme } from "../../../../../../zApps/Layout";
import { User, UserForRanking } from "../../../../../User/types";
import {
    CommenterAvatarElements,
    OriginKey,
    Post,
    UsersConnectionHeightList,
} from "../../types";
import { LeftAvatarArea } from "./LeftAvatarArea";
import { MutedPost } from "./MutedPost";
import { RightContents } from "./RightContents";

export function PostCard({
    post,
    posts,
    setPosts,
    userId,
    tmpMutedUserIds,
    setTmpMutedUserIds,
    user,
    postedUsers,
    unreadCommentNos,
    hideLevelAndCountry,
    hideButtonTitle,
    hideTime,
    scrollableContainerRef,
    originKey,
}: {
    post: Post;
    posts: Post[];
    setPosts: (posts: Post[]) => void;
    userId: number;
    tmpMutedUserIds: number[];
    setTmpMutedUserIds: (ids: number[]) => void;
    user?: User;
    postedUsers: UserForRanking[];
    unreadCommentNos: number[];
    hideLevelAndCountry?: boolean;
    hideButtonTitle?: boolean;
    hideTime: boolean;
    scrollableContainerRef: RefObject<HTMLDivElement>;
    originKey: OriginKey;
}) {
    const [isCommentShown, setCommentShown] = useState(
        unreadCommentNos.length > 0
    );
    const [isInputAreaHidden, setInputAreaHidden] = useState(
        unreadCommentNos.length > 0
    );

    const {
        avatarRef,
        usersConnectionHeightList,
        refreshUsersConnectionHeightList,
        changeCommenterAvatarElement,
        connectionLineStyles,
    } = useUsersConnectionLines();

    const c = useStyles({ isBorderShown: post.postNo < 1 });

    const postedUser = postedUsers.find(u => u.userId === post.userId);
    if (!postedUser) {
        return null;
    }
    if (tmpMutedUserIds.includes(postedUser.userId)) {
        return (
            <MutedPost
                userId={userId}
                post={post}
                tmpMutedUserIds={tmpMutedUserIds}
                setTmpMutedUserIds={setTmpMutedUserIds}
            />
        );
    }

    const isMyPost = postedUser.userId === userId;

    const changePost = (newPostState: Partial<Post>) => {
        setPosts(
            posts.map(p =>
                p.userId === post.userId && p.postNo === post.postNo
                    ? {
                          ...p,
                          ...newPostState,
                      }
                    : p
            )
        );
    };

    return (
        <Collapse in={!post.deleted}>
            <Card
                className={c.card}
                elevation={3}
                onClick={() => {
                    if (isInputAreaHidden) {
                        setInputAreaHidden(false);
                        return;
                    }
                    setCommentShown(!isCommentShown);
                }}
            >
                <LeftAvatarArea
                    postedUser={postedUser}
                    avatarRef={avatarRef}
                    usersConnectionHeightList={usersConnectionHeightList}
                    {...connectionLineStyles}
                />

                <RightContents
                    postedUser={postedUser}
                    post={post}
                    userId={userId}
                    tmpMutedUserIds={tmpMutedUserIds}
                    setTmpMutedUserIds={setTmpMutedUserIds}
                    isMyPost={isMyPost}
                    user={user}
                    deletePost={async () => {
                        if (
                            !confirm(
                                "This post will be deleted.\nAre you sure?"
                            )
                        ) {
                            return;
                        }
                        changePost({
                            deleted: true,
                        });
                        await fetchDeletePosts(userId, post.postNo);
                    }}
                    likePost={async () => {
                        changePost({
                            ownLike: true,
                            likeCount: post.likeCount + 1,
                        });
                        await fetchLikePosts(userId, post.postNo, post.userId);
                        refreshUsersConnectionHeightList();
                    }}
                    unlikePost={async () => {
                        changePost({
                            ownLike: false,
                            likeCount: post.likeCount - 1,
                        });
                        await fetchUnlikePosts(
                            userId,
                            post.postNo,
                            post.userId
                        );
                        refreshUsersConnectionHeightList();
                    }}
                    addCommentCount={(addedCount: number) => {
                        changePost({
                            commentCount: post.commentCount + addedCount,
                        });
                        refreshUsersConnectionHeightList();
                    }}
                    isCommentShown={isCommentShown}
                    unreadCommentNos={unreadCommentNos}
                    isInputAreaHidden={isInputAreaHidden}
                    hideLevelAndCountry={hideLevelAndCountry}
                    hideButtonTitle={hideButtonTitle}
                    changeCommenterAvatarElement={changeCommenterAvatarElement}
                    refreshUsersConnectionHeightList={
                        refreshUsersConnectionHeightList
                    }
                    hideTime={hideTime}
                    scrollableContainerRef={scrollableContainerRef}
                    originKey={originKey}
                />
            </Card>
        </Collapse>
    );
}
const useStyles = makeStyles<Theme, { isBorderShown: boolean }>({
    card: ({ isBorderShown }) => ({
        margin: 5,
        display: "flex",
        padding: "5px 7px 5px 5px",
        border: isBorderShown
            ? `solid 2px ${theme.palette.primary.light}`
            : undefined,
        transition: "all 500ms",
        position: "relative", // users connection lines
    }),
});

function useUsersConnectionLines() {
    let commenterAvatarElements = useMemo<CommenterAvatarElements>(
        () => ({}),
        []
    ); // Don't use useState in case when some comments appear simultaneously

    const avatarRef = useRef<HTMLDivElement>(null);

    const refreshUsersConnectionHeightList = () => {
        setUsersConnectionHeightList(
            getUsersConnectionHeightList(commenterAvatarElements, avatarRef)
        );
    };
    const changeCommenterAvatarElement = (
        type: "add" | "remove",
        commentNo: number,
        element?: HTMLDivElement
    ) => {
        if (type === "remove") {
            delete commenterAvatarElements[commentNo];
        } else {
            if (element) {
                commenterAvatarElements[commentNo] = element;
            }
        }
        refreshUsersConnectionHeightList();
    };

    const [usersConnectionHeightList, setUsersConnectionHeightList] =
        useState<UsersConnectionHeightList>({});

    const { screenWidth } = useScreenSize();
    useEffect(() => {
        refreshUsersConnectionHeightList();
    }, [screenWidth]);

    const avatarEl = avatarRef.current;
    const avatarTop = avatarEl?.offsetTop ?? 0;
    const avatarHeight = avatarEl?.offsetHeight ?? 0;
    const avatarLeft = avatarEl?.offsetLeft ?? 0;
    const avatarWidth = avatarEl?.offsetWidth ?? 0;

    const connectionLineStyles = {
        connectionTop: avatarTop + avatarHeight,
        connectionLeft: avatarLeft + avatarWidth / 2 - 1.5,
        connectionWidth: (avatarRef.current?.offsetWidth ?? 0) / 2 + 27,
    };

    return {
        avatarRef,
        usersConnectionHeightList,
        refreshUsersConnectionHeightList,
        changeCommenterAvatarElement,
        connectionLineStyles,
    };
}

async function fetchDeletePosts(userId: number, postNo: number): Promise<void> {
    await fetchPost<
        {
            userId: number;
            postNo: number;
        },
        undefined
    >("api/Post/DeletePost", { userId, postNo });
}

async function fetchLikePosts(
    userId: number,
    postNo: number,
    postedUserId: number
): Promise<void> {
    await fetchPost<
        {
            userId: number;
            postNo: number;
            postedUserId: number;
        },
        undefined
    >("api/PostComment/LikePost", { userId, postNo, postedUserId });
}

async function fetchUnlikePosts(
    userId: number,
    postNo: number,
    postedUserId: number
): Promise<void> {
    await fetchPost<
        {
            userId: number;
            postNo: number;
            postedUserId: number;
        },
        undefined
    >("api/PostComment/UnlikePost", { userId, postNo, postedUserId });
}

function getUsersConnectionHeightList(
    commenterAvatarElements: CommenterAvatarElements,
    avatarRef: RefObject<HTMLDivElement>
): UsersConnectionHeightList {
    const avatarEl = avatarRef.current;
    return Object.fromEntries(
        Object.entries(commenterAvatarElements).map(co => [
            co[0],
            co[1].offsetTop -
                (avatarEl?.offsetTop ?? 0) -
                (avatarEl?.offsetHeight ?? 0) / 2,
        ])
    );
}
